/**
 * @fileoverview System-web vendor-native violation code set library grid and slideout page.
 * @module system-web/violation-code-sets-page
 *
 * Responsibilities:
 * - Render a local-first grid of cached vendor-native code set artifacts
 * - Show artifact summary/details in the shared GridPageShell slideout
 * - Keep department ICC setup actions out of this library surface
 *
 * Dependencies:
 * - `../services/systemViolationCodeSetLibraryService` for local-first artifact reads
 * - `@ui-admin/components/GridPageShell` for grid chrome and slideout behavior
 *
 * Architecture:
 * - Grid rows come from Dexie-backed artifact reads only
 * - Slideout detail is hydrated from Dexie, not direct API requests
 * - The page represents vendor-native cache/library records rather than tenant-runtime code sets
 *
 * Notes:
 * - ICC credential entry, validation, catalog lookup, and download initiation stay in department detail
 * - This page is a read-first system library surface
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BookOpen, CheckCircle2, RefreshCw, Trash2 } from 'lucide-react';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type { DataGridColumn, DataGridFilter, DataGridRowAction, GridTemplate } from '@ui-admin/components/DataGrid';
import {
  getSystemViolationCodeSetArtifact,
  listSystemViolationCodeSetArtifacts,
  watchSystemViolationCodeSetArtifacts,
  type SystemViolationCodeSetArtifact,
  type SystemViolationCodeSetArtifactRow,
} from '../services/systemViolationCodeSetLibraryService';
import {
  getSystemDb,
  getSystemDexieRaw,
  wakeLocalWrite,
  watchBackgroundJobsBundle,
  type BackgroundJob,
} from '@firesquirrel/local-db';
import {
  archiveCodeSetCache,
  rebuildCodeSetCache,
  verifyCodeSetCache,
  type CodeSetCacheVerifyReport,
} from '../services/codeSetCacheActions';
import CodeSetDetailSlideout from '../components/CodeSetDetailSlideout';
import ConfirmDeleteModal from '../components/ui/ConfirmDeleteModal';

/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Not applicable; this page reads synced system artifact records only.
 *
 * Sync Strategy:
 * - System sync pulls `codeSetRawArtifacts` into Dexie and `liveQuery` refreshes the grid.
 *
 * Conflict Resolution:
 * - Server-authoritative artifact records are treated as read-only in system-web.
 */

function formatDate(dateValue?: string): string {
  if (!dateValue) return '—';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
}

function formatDateTime(dateValue?: string): string {
  if (!dateValue) return '—';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function statusBadgeClass(status: string): string {
  if (status === 'ready' || status === 'completed') return 'bg-green-100 text-green-700 border border-green-200';
  if (status === 'downloading' || status === 'rebuilding') return 'bg-blue-100 text-blue-700 border border-blue-200';
  if (status === 'queued' || status === 'pending') return 'bg-indigo-100 text-indigo-700 border border-indigo-200';
  if (status === 'archiving') return 'bg-amber-100 text-amber-700 border border-amber-200';
  if (status === 'failed' || status === 'corrupted') return 'bg-red-100 text-red-700 border border-red-200';
  return 'bg-gray-100 text-gray-700 border border-gray-200';
}

function formatBytes(bytes: number | undefined): string {
  if (!bytes || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

const COLUMNS: DataGridColumn<SystemViolationCodeSetArtifactRow>[] = [
  {
    id: 'title',
    header: 'Code Set',
    accessorKey: 'title',
    sortable: true,
    minWidth: 260,
    cell: ({ row }) => {
      const artifact = row.original;
      return (
        <div>
          <div className="font-medium text-gray-900">{artifact.title}</div>
          <div className="text-xs text-gray-400 mt-0.5">
            {artifact.vendorLabel} · {artifact.bookId}
          </div>
        </div>
      );
    },
  },
  {
    id: 'vendorLabel',
    header: 'Vendor',
    accessorKey: 'vendorLabel',
    sortable: true,
    width: 160,
    cell: ({ row }) => (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
        {row.original.vendorLabel}
      </span>
    ),
  },
  {
    id: 'edition',
    header: 'Edition',
    accessorKey: 'edition',
    sortable: true,
    width: 110,
    cell: ({ row }) => <span className="text-sm text-gray-600">{row.original.edition || '—'}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    sortable: true,
    width: 120,
    cell: ({ row }) => {
      const status = row.original.status || 'unknown';
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(status)}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
    },
  },
  {
    id: 'chapterCount',
    header: 'Chapters',
    accessorKey: 'chapterCount',
    sortable: true,
    width: 100,
    cell: ({ row }) => <span className="text-sm text-gray-600 tabular-nums">{row.original.chapterCount}</span>,
  },
  {
    id: 'violationCount',
    header: 'Violations',
    accessorKey: 'violationCount',
    sortable: true,
    width: 110,
    cell: ({ row }) => <span className="text-sm text-gray-600 tabular-nums">{row.original.violationCount.toLocaleString()}</span>,
  },
  {
    id: 'totalBytes',
    header: 'Cache Size',
    accessorKey: 'totalBytes',
    sortable: true,
    width: 110,
    cell: ({ row }) => <span className="text-sm text-gray-600 tabular-nums">{formatBytes(row.original.totalBytes)}</span>,
  },
  {
    id: 'downloadedAt',
    header: 'Downloaded',
    accessorKey: 'downloadedAt',
    sortable: true,
    width: 120,
    cell: ({ row }) => <span className="text-sm text-gray-500">{formatDate(row.original.downloadedAt)}</span>,
  },
  {
    id: 'lastVerifiedAt',
    header: 'Last Verified',
    accessorKey: 'lastVerifiedAt',
    sortable: true,
    width: 130,
    cell: ({ row }) => <span className="text-sm text-gray-500">{formatDate(row.original.lastVerifiedAt)}</span>,
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    accessorKey: 'updatedAt',
    sortable: true,
    width: 120,
    cell: ({ row }) => <span className="text-sm text-gray-500">{formatDate(row.original.updatedAt)}</span>,
  },
];

const FILTERS: DataGridFilter[] = [
  {
    id: 'vendorLabel',
    label: 'Vendor',
    type: 'select',
    options: [
      { value: 'ICC Digital Codes', label: 'ICC Digital Codes' },
      { value: 'NFPA LiNK', label: 'NFPA LiNK' },
    ],
  },
  {
    id: 'status',
    label: 'Status',
    type: 'status',
    options: [
      { value: 'ready', label: 'Ready', color: 'green' },
      { value: 'queued', label: 'Queued', color: 'blue' },
      { value: 'downloading', label: 'Downloading', color: 'blue' },
      { value: 'completed', label: 'Completed', color: 'green' },
      { value: 'failed', label: 'Failed', color: 'red' },
    ],
  },
];

const NOW = new Date().toISOString();

function titleFromJob(job: BackgroundJob): string {
  const payload = (job.payload ?? {}) as Record<string, unknown>;
  const payloadTitle = typeof payload.title === 'string' ? payload.title : '';
  if (payloadTitle) return payloadTitle;
  if (job.title.startsWith('Downloading ')) return job.title.replace(/^Downloading\s+/, '');
  if (job.title.startsWith('Rebuilding ')) return job.title.replace(/^Rebuilding\s+/, '');
  return job.title || String(payload.bookId ?? 'Queued Code Set');
}

function statusFromJob(job: BackgroundJob): string {
  if (job.status === 'running') return 'downloading';
  if (job.status === 'failed') return 'failed';
  if (job.status === 'completed') return 'completed';
  return 'queued';
}

function rowFromJob(job: BackgroundJob): SystemViolationCodeSetArtifactRow {
  const payload = (job.payload ?? {}) as Record<string, unknown>;
  const vendorId = typeof payload.vendorId === 'string' ? payload.vendorId : 'icc';
  const bookId = typeof payload.bookId === 'string' ? payload.bookId : job._id;
  const phase = job.message ?? job.progress?.phase ?? job.status;
  const editionValue = typeof payload.edition === 'string' ? payload.edition : '';
  const percent = typeof job.progress?.percent === 'number' ? ` · ${Math.round(job.progress.percent)}%` : '';
  const edition = editionValue ? `${editionValue} · ${phase}${percent}` : `${phase}${percent}`;
  const violationCount = typeof job.progress?.total === 'number' ? job.progress.total : 0;
  // Build the real server-recognised cacheKey ({vendor}:{book}:{printing}) from the
  // download payload so row-level actions (Delete, Rebuild, Verify) can call the
  // code-set-cache admin routes for in-progress downloads, not just finished artifacts.
  const cacheKey = editionValue
    ? `${vendorId}:${bookId}:${editionValue}`
    : `${vendorId}:${bookId}`;
  return {
    _id: `job:${job._id}`,
    vendorId,
    vendorLabel: vendorId === 'icc' ? 'ICC Digital Codes' : vendorId === 'nfpa' ? 'NFPA LiNK' : vendorId.toUpperCase(),
    title: titleFromJob(job),
    edition,
    bookId,
    status: statusFromJob(job),
    chapterCount: 0,
    violationCount,
    totalBytes: 0,
    downloadedAt: job.startedAt,
    lastVerifiedAt: undefined,
    updatedAt: job.updatedAt,
    cacheKey,
  };
}


function mergeArtifactAndJobRows(
  artifactRows: SystemViolationCodeSetArtifactRow[],
  backgroundJobRows: SystemViolationCodeSetArtifactRow[],
): SystemViolationCodeSetArtifactRow[] {
  const artifactKeys = new Set(
    artifactRows.map((row) => `${row.vendorId}:${row.bookId}:${row.edition ?? ''}`),
  );
  const visibleJobs = backgroundJobRows.filter((row) => (
    row.status !== 'completed'
    || !artifactKeys.has(`${row.vendorId}:${row.bookId}:${String(row.edition ?? '').split(' · ')[0]}`)
  ));
  return [...visibleJobs, ...artifactRows];
}

function jobMatchesArtifactRow(job: BackgroundJob, row: SystemViolationCodeSetArtifactRow): boolean {
  const payload = (job.payload ?? {}) as Record<string, unknown>;
  const vendorId = typeof payload.vendorId === 'string' ? payload.vendorId : '';
  const bookId = typeof payload.bookId === 'string' ? payload.bookId : '';
  const edition = typeof payload.edition === 'string' ? normalizeEditionKey(payload.edition) : '';
  const rowEdition = normalizeEditionKey(String(row.edition ?? '').split(' · ')[0]);
  return vendorId === row.vendorId && bookId === row.bookId && (!edition || !rowEdition || edition === rowEdition);
}

function normalizeEditionKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[:]+/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function overlayActiveJobStatus(
  artifactRows: SystemViolationCodeSetArtifactRow[],
  activeJobs: BackgroundJob[],
): SystemViolationCodeSetArtifactRow[] {
  return artifactRows.map((row) => {
    const job = activeJobs.find((candidate) => (
      (candidate.status === 'running' || candidate.status === 'pending')
      && jobMatchesArtifactRow(candidate, row)
    ));
    if (!job) return row;
    const phase = job.message ?? job.progress?.phase ?? job.status;
    const percent = typeof job.progress?.percent === 'number' ? ` · ${Math.round(job.progress.percent)}%` : '';
    const edition = `${String(row.edition ?? '').split(' · ')[0].trim() || '—'} · ${phase}${percent}`;
    return {
      ...row,
      status: statusFromJob(job),
      edition,
      updatedAt: job.updatedAt ?? row.updatedAt,
      violationCount: typeof job.progress?.total === 'number' ? job.progress.total : row.violationCount,
    };
  });
}


const SEED_TEMPLATES: GridTemplate[] = [
  {
    _id: 'seed-system-violation-code-sets-all',
    gridType: 'system-violation-code-sets',
    name: 'All Cached Code Sets',
    isDefault: true,
    scope: 'system',
    createdBy: { userId: 'system', displayName: 'System' },
    usageCount: 0,
    createdAt: NOW,
    updatedAt: NOW,
    state: {
      columnVisibility: {},
      columnOrder: ['title', 'vendorLabel', 'edition', 'status', 'chapterCount', 'violationCount', 'totalBytes', 'downloadedAt', 'lastVerifiedAt', 'updatedAt'],
      sorting: [{ id: 'downloadedAt', desc: true }],
      filterValues: {},
      pageSize: 25,
      searchText: '',
    },
  },
  {
    _id: 'seed-system-violation-code-sets-ready',
    gridType: 'system-violation-code-sets',
    name: 'Ready Artifacts',
    isDefault: false,
    scope: 'system',
    createdBy: { userId: 'system', displayName: 'System' },
    usageCount: 0,
    createdAt: NOW,
    updatedAt: NOW,
    state: {
      columnVisibility: {},
      columnOrder: ['title', 'vendorLabel', 'edition', 'status', 'chapterCount', 'violationCount', 'totalBytes', 'downloadedAt', 'lastVerifiedAt'],
      sorting: [{ id: 'downloadedAt', desc: true }],
      filterValues: { status: 'ready' },
      pageSize: 25,
      searchText: '',
    },
  },
];

/**
 * @component SystemViolationCodeSetsPage
 *
 * Purpose:
 * - Provides a system-web library surface for browsing synced vendor-native code set artifacts.
 *
 * Props:
 * - None.
 *
 * Behavior:
 * - Loads artifact rows from the local-first service, subscribes to Dexie updates, and opens a slideout for the selected artifact.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Must remain read-only and must not call API endpoints directly.
 *
 * Performance:
 * - Uses memoized subtitle text and Dexie-backed subscriptions to avoid manual polling.
 */
export default function SystemViolationCodeSetsPage() {
  const [artifacts, setArtifacts] = useState<SystemViolationCodeSetArtifactRow[]>([]);
  const [jobs, setJobs] = useState<BackgroundJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);
  const [selectedArtifact, setSelectedArtifact] = useState<SystemViolationCodeSetArtifact | null>(null);
  const [busyAction, setBusyAction] = useState<'resume' | 'restart' | 'archive' | 'verify' | null>(null);
  const [verifyReport, setVerifyReport] = useState<CodeSetCacheVerifyReport | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [deleteProgress, setDeleteProgress] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    title: string;
    cacheKey: string;
    rowId: string;
    closePanel: boolean;
  } | null>(null);

  const loadArtifacts = useCallback(async () => {
    setLoading(true);
    try {
      wakeLocalWrite('code_set_raw_artifacts');
      const rows = await listSystemViolationCodeSetArtifacts();
      setArtifacts(rows);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadArtifacts();
    const unsubscribe = watchSystemViolationCodeSetArtifacts(
      (rows) => {
        setArtifacts(rows);
        setLoading(false);
      },
      () => {
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [loadArtifacts]);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    void getSystemDb()
      .then(() => {
        if (cancelled) return;
        const raw = getSystemDexieRaw();
        if (!raw) return;
        const subscription = watchBackgroundJobsBundle(raw, 25).subscribe({
          next: (bundle) => {
            if (cancelled) return;
            const filtered = [...bundle.active, ...bundle.recentlyFinished]
              .filter((job) => job.jobType === 'codeSetDownload')
              .filter((job) => job.status !== 'cancelled');
            setJobs(filtered);
          },
          error: (error) => {
            console.warn('[SystemViolationCodeSetsPage] background job watch failed:', error);
          },
        });
        unsubscribe = () => subscription.unsubscribe();
      })
      .catch((error) => {
        console.warn('[SystemViolationCodeSetsPage] failed to open system Dexie for background jobs:', error);
      });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!selectedArtifactId || selectedArtifactId.startsWith('job:')) {
      setSelectedArtifact(null);
      setVerifyReport(null);
      setActionError(null);
      return;
    }
    // Clear prior verify report + error when a new row is selected.
    setVerifyReport(null);
    setActionError(null);
    let cancelled = false;
    getSystemViolationCodeSetArtifact(selectedArtifactId)
      .then((artifact) => {
        if (!cancelled) {
          setSelectedArtifact(artifact);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedArtifact(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedArtifactId]);

  useEffect(() => {
    if (!actionNotice) return undefined;
    const timer = window.setTimeout(() => setActionNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [actionNotice]);

  const selectedJob = useMemo<BackgroundJob | null>(() => {
    if (!selectedArtifactId) return null;
    if (!selectedArtifactId.startsWith('job:')) {
      const selectedRow = artifacts.find((artifact) => artifact._id === selectedArtifactId);
      if (!selectedRow) return null;
      return jobs.find((job) => jobMatchesArtifactRow(job, selectedRow)) ?? null;
    }
    const jobId = selectedArtifactId.slice('job:'.length);
    return jobs.find((job) => job._id === jobId) ?? null;
  }, [artifacts, selectedArtifactId, jobs]);

  const handleRebuild = useCallback(async (mode: 'resume' | 'restart' = 'resume') => {
    if (!selectedArtifact) return;
    setBusyAction(mode);
    setActionError(null);
    try {
      await rebuildCodeSetCache(selectedArtifact.cacheKey, mode);
      setActionNotice(mode === 'restart'
        ? 'Restart queued. Cached blobs will be deleted by the worker before re-download.'
        : 'Resume queued. Existing completed root blobs will be reused where possible.');
      // The manifest row flips to `rebuilding` server-side; Dexie sync will
      // re-project the row via the usual change-stream path.
    } catch (err) {
      setActionError((err as Error).message || `${mode === 'restart' ? 'Restart' : 'Resume'} failed`);
    } finally {
      setBusyAction(null);
    }
  }, [selectedArtifact]);

  const handleArchive = useCallback(() => {
    if (!selectedArtifact) return;
    setDeleteProgress(null);
    setDeleteConfirm({
      title: selectedArtifact.title || selectedArtifact.cacheKey,
      cacheKey: selectedArtifact.cacheKey,
      rowId: selectedArtifact._id,
      closePanel: true,
    });
  }, [selectedArtifact]);

  const handleVerify = useCallback(async () => {
    if (!selectedArtifact) return;
    setBusyAction('verify');
    setActionError(null);
    try {
      const report = await verifyCodeSetCache(selectedArtifact.cacheKey);
      setVerifyReport(report);
    } catch (err) {
      setActionError((err as Error).message || 'Verify failed');
    } finally {
      setBusyAction(null);
    }
  }, [selectedArtifact]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirm) return;
    setBusyAction('archive');
    setActionError(null);
    setActionNotice(null);
    setDeleteProgress('Cleaning up Azure Blob Storage and removing the server manifest…');
    try {
      const result = await archiveCodeSetCache(deleteConfirm.cacheKey);
      if (result.blobDeleteError) {
        setDeleteProgress('Blob cleanup failed before the local record was removed. You can retry or discard.');
        throw new Error(result.blobDeleteError);
      }
      setDeleteProgress(`Blob cleanup complete. Removed ${result.blobsDeleted} cached blob${result.blobsDeleted === 1 ? '' : 's'}. Removing the local record…`);

      const db = await getSystemDb();
      await db.code_set_raw_artifacts.delete(deleteConfirm.rowId);

      if (deleteConfirm.closePanel) {
        setSelectedArtifactId(null);
        setSelectedArtifact(null);
      }
      setDeleteConfirm(null);
      setActionNotice(`Deleted "${deleteConfirm.title}". Blob cleanup confirmed (${result.blobsDeleted} blob${result.blobsDeleted === 1 ? '' : 's'} removed).`);
    } catch (err) {
      setActionError((err as Error).message || 'Delete failed');
      setDeleteProgress('Delete failed before the local record was removed. You can retry or discard.');
    } finally {
      setBusyAction(null);
    }
  }, [deleteConfirm]);

  // Grid rows come exclusively from the artifact Dexie table. The system-api
  // enqueue route seeds a `codeSetRawArtifacts` row with status='queued' the
  // moment a download is enqueued, so queued/downloading/ready books all flow
  // through this single source of truth. backgroundJobs stays available to the
  // slideout for live progress, but never synthesizes grid rows anymore.
  const gridRows = useMemo(() => overlayActiveJobStatus(artifacts, jobs), [artifacts, jobs]);

  const panelSubtitleText = useMemo(() => {
    if (selectedArtifact) return `${selectedArtifact.vendorId.toUpperCase()} · ${selectedArtifact.bookId}`;
    if (selectedJob) {
      const payload = (selectedJob.payload ?? {}) as Record<string, unknown>;
      return `${String(payload.vendorId ?? 'icc').toUpperCase()} · ${String(payload.bookId ?? selectedJob._id)}`;
    }
    return undefined;
  }, [selectedArtifact, selectedJob]);

  useEffect(() => {
    if (!selectedArtifactId) return;
    if (selectedArtifactId.startsWith('job:')) return;
    if (!artifacts.find((artifact) => artifact._id === selectedArtifactId)) {
      setSelectedArtifactId(null);
      setSelectedArtifact(null);
    }
  }, [artifacts, selectedArtifactId]);

  const subtitle = useMemo(() => {
    if (loading) {
      return 'Loading cached vendor-native violation code set artifacts…';
    }
    const activeJobCount = artifacts.filter((row) => row.status === 'queued' || row.status === 'downloading').length;
    const artifactLabel = `${artifacts.length} ${artifacts.length === 1 ? 'cached artifact' : 'cached artifacts'}`;
    return activeJobCount > 0
      ? `${artifactLabel} available · ${activeJobCount} code-set ${activeJobCount === 1 ? 'download' : 'downloads'} in progress`
      : `${artifactLabel} available in the system library`;
  }, [artifacts, loading]);

  const handleRowClick = useCallback((row: SystemViolationCodeSetArtifactRow) => {
    setSelectedArtifactId(row._id);
  }, []);

  /**
   * @process DeleteCachedCodeSetFromRow
   *
   * Steps:
   * 1. Confirm destructive intent with the operator.
   * 2. Call archiveCodeSetCache(cacheKey) — DELETE removes vendor blobs + manifest row.
   * 3. Close the slideout if the deleted row was active so stale state does not linger.
   *
   * Inputs:
   * - row: SystemViolationCodeSetArtifactRow with cacheKey populated.
   *
   * Outputs:
   * - Manifest row disappears from Dexie on next sync tick; UI reflects via liveQuery.
   *
   * Failure Handling:
   * - Surface error via actionError state.
   *
   * Idempotency:
   * - REQUIRED — repeated deletes are server-side no-ops once the row is gone.
   */
  const handleRowDelete = useCallback((row: SystemViolationCodeSetArtifactRow) => {
    if (!row.cacheKey) return;
    setDeleteProgress(null);
    setDeleteConfirm({
      title: row.title || row.cacheKey,
      cacheKey: row.cacheKey,
      rowId: row._id,
      closePanel: selectedArtifactId === row._id,
    });
  }, [selectedArtifactId]);

  const rowActions = useMemo<DataGridRowAction<SystemViolationCodeSetArtifactRow>[]>(
    () => [
      {
        id: 'delete',
        label: 'Delete',
        icon: Trash2,
        variant: 'danger' as const,
        onClick: (row) => { void handleRowDelete(row); },
      },
    ],
    [handleRowDelete],
  );

  return (<>
    {actionNotice && (
      <div className="mx-6 mt-3 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 shrink-0">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        <span className="flex-1">{actionNotice}</span>
        <button onClick={() => setActionNotice(null)} className="shrink-0 text-green-500 hover:text-green-700 ml-2">✕</button>
      </div>
    )}
    <GridPageShell<SystemViolationCodeSetArtifactRow>
      gridId="system-violation-code-sets"
      gridType="system-violation-code-sets"
      title="Violation Code Sets"
      subtitle={subtitle}
      columns={COLUMNS}
      data={gridRows}
      getRowId={(row) => row._id}
      filters={FILTERS}
      seedTemplates={SEED_TEMPLATES}
      onRowClick={handleRowClick}
      activeRowId={selectedArtifactId}
      rowActions={rowActions}
      searchPlaceholder="Search cached code set artifacts…"
      toolbarActions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => void loadArtifacts()}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      }
      emptyIcon={BookOpen}
      emptyTitle="No cached violation code sets"
      emptySubtitle="Downloaded vendor-native code set artifacts will appear here after they sync into the system library cache."
      panelContent={(selectedArtifact || selectedJob) ? (
        <CodeSetDetailSlideout
          vendorId={selectedArtifact?.vendorId ?? String((selectedJob?.payload ?? {}).vendorId ?? 'icc')}
          bookId={selectedArtifact?.bookId ?? String((selectedJob?.payload ?? {}).bookId ?? selectedJob?._id ?? '')}
          title={selectedArtifact?.title ?? (selectedJob ? titleFromJob(selectedJob) : '')}
          artifact={selectedArtifact}
          job={selectedJob}
          backgroundJobs={jobs}
          onRebuild={handleRebuild}
          onArchive={handleArchive}
          onVerify={handleVerify}
          busyAction={busyAction}
          verifyReport={verifyReport}
          actionError={actionError}
        />
      ) : null}
      panelTitle={selectedArtifact?.title ?? (selectedJob ? titleFromJob(selectedJob) : undefined)}
      panelSubtitle={panelSubtitleText}
      onPanelClose={() => setSelectedArtifactId(null)}
      onRefresh={() => void loadArtifacts()}
      exportable
      selectable
    />

    {deleteConfirm && (
      <ConfirmDeleteModal
        title="Delete Cached Code Set?"
        confirmValue={deleteConfirm.title}
        confirmLabel="code set title"
        description="This permanently removes the cached vendor blobs and manifest row from system storage. Tenant inspection data is preserved, but tenants using this book will need to switch to a different source."
        warning="This action cannot be undone. The vendor blobs will be permanently deleted from Azure Blob Storage."
        extraContent={deleteProgress ? (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs font-medium text-blue-800">
            {deleteProgress}
          </div>
        ) : null}
        deleteButtonLabel="Delete Code Set"
        isSubmitting={busyAction === 'archive'}
        onConfirm={() => { void handleDeleteConfirm(); }}
        onClose={() => {
          setDeleteProgress(null);
          setDeleteConfirm(null);
        }}
      />
    )}
  </>);
}
